<template>
  <Modal :value="visible" title="调整代理商上级" :width="620" :mask-closable="false" :closable="!saving && !confirming" footer-hide @on-cancel="close">
    <Spin v-if="loading" />
    <template v-else>
      <Alert v-if="loadError" type="error" show-icon>
        {{ loadError }}
        <Button type="text" @click="load">重试</Button>
      </Alert>
      <template v-else-if="currentAgent">
        <Alert type="warning" show-icon>
          调整后，该代理商及其下级分支会一起变更归属。原上级与新上级的直属代管权限将按新关系生效。
        </Alert>
        <Form :label-width="100">
          <FormItem label="代理商">{{ currentAgent.name }}（#{{ currentAgent.id }}）</FormItem>
          <FormItem label="当前上级">
            {{ currentParentLabel }}
            <Button type="text" :disabled="saving || confirming" @click="load">刷新关系</Button>
          </FormItem>
          <FormItem label="新上级">
            <Select v-model="parentAgentId" filterable remote clearable transfer
              :label="selectedParent ? selectedParent.name : ''"
              :disabled="saving || confirming" :loading="optionsLoading"
              :remote-method="searchParents" placeholder="搜索代理商；留空恢复为一级代理商" @on-change="selectParent">
              <Option v-for="item in displayedOptions" :key="item.id" :value="Number(item.id)"
                :disabled="Number(item.id) === Number(currentAgent.id) || item.status !== 1">
                {{ item.name }}（#{{ item.id }}{{ item.status !== 1 ? '，已禁用' : '' }}）
              </Option>
            </Select>
            <Page v-if="total > 20" :current="page" :total="total" :page-size="20" size="small" simple @on-change="changePage" />
            <Button type="text" :disabled="saving || confirming" @click="setRoot">恢复为一级代理商</Button>
            <p>不能选择自己或自己的下级分支作为上级。</p>
          </FormItem>
        </Form>
        <div class="parent-change-actions">
          <Button :disabled="saving || confirming" @click="close">取消</Button>
          <Button type="primary" :loading="saving" :disabled="confirming || !changed" @click="confirmChange">保存调整</Button>
        </div>
      </template>
    </template>
  </Modal>
</template>

<script>
import { getAgentInfo, getAgentList, changeAgentParent } from '@/api/agent'

const escapeHtml = text => String(text).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[char]))

export default {
  name: 'AgentParentChange',
  props: {
    visible: { type: Boolean, default: false },
    agent: { type: Object, default: null }
  },
  data () {
    return {
      currentAgent: null,
      currentParent: null,
      parentAgentId: null,
      selectedParent: null,
      loading: false,
      loadError: '',
      saving: false,
      confirming: false,
      options: [],
      optionsLoading: false,
      page: 1,
      total: 0,
      keyword: '',
      requestId: 0,
      optionsRequestId: 0,
      searchTimer: null
    }
  },
  computed: {
    currentParentLabel () {
      if (!this.currentAgent || !this.currentAgent.parentAgentId) return '一级代理商'
      return `${this.currentParent ? this.currentParent.name : '代理商'}（#${this.currentAgent.parentAgentId}）`
    },
    displayedOptions () {
      const items = this.options.slice()
      if (this.selectedParent && !items.some(item => Number(item.id) === Number(this.selectedParent.id))) items.unshift(this.selectedParent)
      return items
    },
    changed () {
      return this.currentAgent && (this.parentAgentId || null) !== (this.currentAgent.parentAgentId || null)
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (visible) {
        if (visible) this.load()
        else this.invalidateRequests()
      }
    },
    agent (agent) {
      if (this.visible && agent && !this.saving && !this.confirming) this.load()
    }
  },
  methods: {
    invalidateRequests () {
      this.requestId += 1
      this.optionsRequestId += 1
      window.clearTimeout(this.searchTimer)
    },
    close () {
      if (this.saving || this.confirming) return
      this.invalidateRequests()
      this.$emit('update:visible', false)
    },
    async load () {
      if (!this.agent) return
      this.invalidateRequests()
      const requestId = ++this.requestId
      this.loading = true
      this.loadError = ''
      this.currentAgent = null
      this.currentParent = null
      this.parentAgentId = null
      this.selectedParent = null
      this.options = []
      this.page = 1
      this.keyword = ''
      try {
        const res = await getAgentInfo(this.agent.id)
        if (requestId !== this.requestId || !this.visible) return
        if (res.data.code !== 200 || !res.data.data) throw new Error(res.data.msg || '获取代理商失败')
        this.currentAgent = res.data.data
        this.parentAgentId = this.currentAgent.parentAgentId || null
        if (this.parentAgentId) {
          const parent = await getAgentInfo(this.parentAgentId)
          if (requestId !== this.requestId || !this.visible) return
          if (parent.data.code !== 200 || !parent.data.data) throw new Error(parent.data.msg || '获取当前上级失败')
          this.currentParent = parent.data.data
          this.selectedParent = parent.data.data
        }
        await this.loadOptions()
      } catch (err) {
        if (requestId === this.requestId) this.loadError = this.errorMessage(err, '获取代理商失败')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    searchParents (keyword) {
      window.clearTimeout(this.searchTimer)
      this.optionsRequestId += 1
      this.searchTimer = window.setTimeout(() => {
        this.keyword = String(keyword || '').trim()
        this.page = 1
        this.loadOptions()
      }, 300)
    },
    changePage (page) {
      if (this.saving || this.confirming) return
      this.page = page
      this.loadOptions()
    },
    async loadOptions () {
      const requestId = ++this.optionsRequestId
      this.optionsLoading = true
      try {
        const res = await getAgentList({ page: this.page, pageSize: 20, keyword: this.keyword || undefined })
        if (requestId !== this.optionsRequestId || !this.visible) return
        if (res.data.code !== 200) throw new Error(res.data.msg || '获取上级代理商列表失败')
        this.options = res.data.data.records || []
        this.total = Number(res.data.data.total) || 0
      } catch (err) {
        if (requestId === this.optionsRequestId) this.$Message.error(this.errorMessage(err, '获取上级代理商列表失败'))
      } finally {
        if (requestId === this.optionsRequestId) this.optionsLoading = false
      }
    },
    selectParent (id) {
      this.parentAgentId = id || null
      this.selectedParent = id ? this.displayedOptions.find(item => Number(item.id) === Number(id)) || null : null
    },
    setRoot () {
      this.selectParent(null)
    },
    errorMessage (err, fallback) {
      return (err.response && err.response.data && err.response.data.msg) || err.message || fallback
    },
    confirmChange () {
      if (this.saving || this.confirming || !this.currentAgent || !this.changed) return
      const payload = {
        parentAgentId: this.parentAgentId || null,
        restoreRoot: !this.parentAgentId,
        expectedParentAgentId: this.currentAgent.parentAgentId || null,
        checkExpectedParent: true
      }
      const agentId = this.currentAgent.id
      const next = this.selectedParent ? `${this.selectedParent.name}（#${payload.parentAgentId}）` : '一级代理商'
      this.confirming = true
      this.$Modal.confirm({
        title: '确认调整上级',
        content: `<p>代理商：${escapeHtml(this.currentAgent.name)}</p><p>当前上级：${escapeHtml(this.currentParentLabel)}</p><p>调整为：${escapeHtml(next)}</p><p>该代理商的下级分支会随之变更归属。</p>`,
        onOk: () => { this.confirming = false; return this.save(payload, agentId) },
        onCancel: () => { this.confirming = false }
      })
    },
    async save (payload, agentId) {
      if (this.saving || !this.visible || !this.currentAgent) return
      if (Number(this.currentAgent.id) !== Number(agentId)) {
        this.$Message.error('代理商已切换，请重新确认')
        return
      }
      this.saving = true
      try {
        const res = await changeAgentParent(agentId, payload)
        if (res.data.code !== 200) throw new Error(res.data.msg || '调整上级失败')
        this.$Message.success('代理商上级已调整')
        this.$emit('saved', res.data.data)
        this.$emit('update:visible', false)
      } catch (err) {
        this.$Message.error(this.errorMessage(err, '调整上级失败'))
      } finally {
        this.saving = false
      }
    }
  },
  beforeDestroy () {
    this.invalidateRequests()
  }
}
</script>

<style scoped>
.parent-change-actions { display: flex; justify-content: flex-end; gap: 10px; }
</style>
